import { Loader } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import LoadOrderAdjustmentCard from '@/components/restocking/LoadOrderAdjustmentCard';

import { DEFAULT_ERROR_MESSAGE } from '@/constants/app/config';
import { sleep } from '@/helpers/sleep';
import {
  useEditRestockMutation,
  useGetRestockRequestQuery,
} from '@/store/api/services/restocking/restocking';

import type { CassetteView } from '@/types/generated/athena';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const CreateLoadOrderAdjustment = ({ props, hide }: ModalChildrenProps) => {
  const id = props?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [adjustmentReason, setAdjustmentReason] = useState<string>('');
  const [config, setConfig] = useState<CassetteView[]>([]);
  const [editRestock] = useEditRestockMutation();
  const [bagId, setBagId] = useState<string>('');

  const {
    data: { data: order = null } = {},
    isLoading: isLoadingOrder,
    error,
  } = useGetRestockRequestQuery(id ?? skipToken);

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      await editRestock({
        id,
        data: {
          adjustment_reason: adjustmentReason,
          restock_type: order?.restock_type,
          status: order?.status,
          loaded_bag_id: bagId,
          cassettes: [...(order?.cassettes ?? [])]
            .filter(({ bill_note_id }) => bill_note_id)
            .map(({ bill_note_id, cassette_index, count }) => ({
              bill_note_id,
              cassette_index,
              notes_count:
                (config.find((item) => item.cassette_index === cassette_index)?.count ?? 0) +
                (count ?? 0),
            })),
        },
      }).unwrap();
      await sleep(1000);
      hide();
      toast.success('Adjustment created successfully');
    } catch (error) {
      toast.error((error as any).message ?? DEFAULT_ERROR_MESSAGE);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (error) hide();
  }, [error]);

  useEffect(() => {
    setBagId(order?.bag_id ?? '');
    setConfig(
      [...(order?.cassettes ?? [])]
        .filter(({ bill_note_id }) => bill_note_id)
        .map(({ cassette_index }) => ({
          cassette_index,
          count: 0,
        })),
    );
  }, [order]);

  const handleChange = (cassette: CassetteView) =>
    setConfig(
      config.map((item) =>
        item.cassette_index === cassette.cassette_index ? { ...item, count: cassette.count } : item,
      ),
    );

  const isValid =
    config?.some(({ count }) => count !== 0) && !!adjustmentReason && bagId.length > 3;

  if (isLoadingOrder) return <Loader />;
  return (
    <>
      <DialogContent>
        <Stack gap={3} py={3}>
          <Box>
            <Typography variant="h6" mb={1}>
              Load Bag ID *
            </Typography>
            <TextField
              onChange={({ target }) => setBagId(target.value.toUpperCase())}
              placeholder="Bag ID"
              disabled={isLoading}
              value={bagId}
              fullWidth
            />
          </Box>
          {order?.cassettes?.map((cassette, idx) => (
            <LoadOrderAdjustmentCard onChange={handleChange} key={idx} cassette={cassette} />
          ))}
          <Stack gap={1}>
            <Typography variant="h6">Adjustment Reason *</Typography>
            <TextField
              value={adjustmentReason}
              onChange={({ target }) => setAdjustmentReason(target.value)}
              placeholder="Adjustment Note"
              variant="standard"
              fullWidth
              multiline
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={hide}>Close</Button>
        <LoadingButton
          loading={isLoading}
          disabled={!isValid}
          onClick={handleUpdate}
          variant="contained"
        >
          Update
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CreateLoadOrderAdjustment;
